import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';

import { SESSION_STORAGE_VARIABLES } from '@app/services/storageManager';
import { NetworkLink } from '@app/router/constants';
import { useNavigate } from '@app/hooks';
import { resetDiagnosticData, runDiagnostic, selectNetworkIssues, setRunDate } from '@app/redux/network/networkSlice';

import { DiagnosticCardContent } from './diagnostic-card-content';
import { NetworkIssues } from './network-issues';
import { RunLimitNotifier } from './run-limit-notifier';

import styles from './styles.module.scss';

const MAX_BUTTON_CLICKS = 3;

const TIME_LIMIT = 15 * 60 * 1000;

export const RunDiagnosticCard = () => {
  const dispatch = useDispatch();

  const { goTo } = useNavigate();
  const networkIssues = useSelector(selectNetworkIssues);

  const timeStampsFromStorage = localStorage.getItem(SESSION_STORAGE_VARIABLES.DIAGNOSTIC_RUNS_TIMESTAMPS);

  const [timeStamps, setTimeStamps] = useState<number[]>(JSON.parse(timeStampsFromStorage || '[]'));
  const [buttonLimitExceded, setButtonLimitExceded] = useState(false);

  useEffect(() => {
    localStorage.setItem(SESSION_STORAGE_VARIABLES.DIAGNOSTIC_RUNS_TIMESTAMPS, JSON.stringify(timeStamps));
  }, [timeStamps]);

  const navigateToDiagnosticReport = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    goTo(`${NetworkLink.Network}/${NetworkLink.DiagnosticResults}`);
  };

  const _runDiagnostic = () => {
    dispatch(runDiagnostic());

    const runDate = format(new Date(Date.now()), 'MMM do HH:mm a zzz');
    dispatch(setRunDate(runDate));
  };

  const runDiagnosticHandler = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();

    const clickTime = Date.now();

    const cannotRunAgain = clickTime - timeStamps[0] < TIME_LIMIT;

    const runLimitReached = timeStamps.length >= MAX_BUTTON_CLICKS && cannotRunAgain;

    if (runLimitReached) {
      dispatch(resetDiagnosticData());
      setButtonLimitExceded(true);
      return;
    }

    const newTimeStamps = timeStamps.slice();
    newTimeStamps.push(clickTime);
    newTimeStamps.splice(0, newTimeStamps.length - MAX_BUTTON_CLICKS);

    setTimeStamps(newTimeStamps);

    _runDiagnostic();
  };

  if (buttonLimitExceded) {
    return <RunLimitNotifier navigateToDiagnosticReport={navigateToDiagnosticReport} />;
  }

  if (networkIssues) {
    return (
      <NetworkIssues
        navigateToDiagnosticReport={navigateToDiagnosticReport}
        runDiagnosticHandler={runDiagnosticHandler}
        networkIssues={networkIssues}
      />
    );
  }

  return (
    <div className={styles['card']}>
      <DiagnosticCardContent
        runDiagnosticHandler={runDiagnosticHandler}
        navigateToDiagnosticReport={navigateToDiagnosticReport}
      />
    </div>
  );
};
